import { hasPremiumFeature } from '@lub0v/santa-utils';
import { BrowserPreview } from '@src/components/BrowserPreview/BrowserPreview';
import { GamePreview } from '@src/pages/NewBoxPage/AppearancePicker';
import { useTheme } from '@src/theme/useTheme';
import { PremiumUpsell } from '@src/components/PremiumUpsell/PremiumUpsell';
import { useRef, useState, type MouseEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  PREMIUM_BOX_NAME_MAX_LENGTH,
  PENDING_PREMIUM_BOX_KEY_PREFIX,
  pendingPremiumBoxName,
  premiumBoxNameError,
  type BoxInfo,
} from '@lub0v/santa-utils';
import {
  Button,
  Hint,
  Input,
  Switch,
  Stack,
  Typography,
} from '@src/components';
import { loadSession } from '@src/state/sessionSlice';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { mainSiteUrl, premiumGameUrl } from '@src/routing/boxDomainNavigation';
import { isDotCom } from '@src/site';
import { LinkIcon } from '@src/components/icons/LinkIcon';
import { OrganizerPanelSection } from './OrganizerPanelSection';
import { useMobileBoxLayout } from './BoxLayouts';
import './BoxLinkPanel.css';

export function OrganizerPanelBoxLink({ box }: { box: BoxInfo['box'] }) {
  const { t, i18n } = useTranslation();
  const mobile = useMobileBoxLayout();
  const { seasonPackage, theme } = useTheme();
  const pendingName = pendingPremiumBoxName(box.premiumKey);
  const dispatch = useAppDispatch();
  const [enabled, setEnabled] = useState(Boolean(pendingName));
  const [desiredName, setDesiredName] = useState(pendingName ?? '');
  const [submittedName, setSubmittedName] = useState(pendingName);
  const [busy, setBusy] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [availableName, setAvailableName] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const nameError = premiumBoxNameError(desiredName);
  const validationVersion = useRef(0);
  const validation = useRef<{ name: string; promise: Promise<boolean> } | null>(null);
  const contactEmail = useAppSelector((state) => state.session.user?.email?.trim());
  const canEnable = hasPremiumFeature(box, 'boxDomain') && !box.isArchived;
  const dotCom = typeof window === 'undefined' ? i18n.language === 'en' : isDotCom();
  const domain = `santa-secret.${dotCom ? 'com' : 'ru'}`;
  const support = `postman@${domain}`;
  const editing = canEnable && enabled;
  const approvedName = hasPremiumFeature(box, 'boxDomain') && !submittedName && !pendingName ? box.premiumKey : null;
  const approvedGameUrl = approvedName ? premiumGameUrl(box) : '';
  const approvedUrl = approvedGameUrl.startsWith('/')
    ? `https://${approvedName}.${domain}`
    : approvedGameUrl;
  const approvedJoinUrl =
    approvedUrl && box.inviteKey
      ? (() => {
          const target = new URL(approvedUrl);
          target.searchParams.set('join', box.inviteKey);
          return `${target.origin}${target.search}${target.hash}`;
        })()
      : approvedUrl;
  const usingApprovedAddress = Boolean(
    approvedUrl &&
    typeof window !== 'undefined' &&
    new URL(approvedUrl).origin === window.location.origin,
  );
  function openInvitationPanel(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const panel = document.getElementById('invite');
    if (!(panel instanceof HTMLDetailsElement)) return;
    event.preventDefault();
    panel.open = true;
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function useApprovedAddress() {
    const target = new URL('settings', approvedUrl);
    target.search = window.location.search;
    target.hash = '#game-address';
    window.location.assign(target.href);
  }

  function addressError(code?: string) {
    const errorKey =
      code === 'PREMIUM_ADDRESS_TAKEN'
        ? 'requestTaken'
        : code === 'PREMIUM_ADDRESS_EMAIL_REQUIRED'
          ? 'requestEmailRequired'
          : code === 'PREMIUM_ADDRESS_RESERVED'
            ? 'nameErrors.reserved'
            : code === 'PREMIUM_ADDRESS_INVALID'
              ? 'nameErrors.format'
              : 'requestFailed';
    return t(`game.settings.boxLink.${errorKey}`);
  }
  async function validateAddress(): Promise<boolean> {
    setNameTouched(true);
    if (!canEnable || nameError) return false;
    if (validation.current?.name === desiredName) return validation.current.promise;
    const name = desiredName;
    setAvailableName(null);
    const version = ++validationVersion.current;
    const promise = (async () => {
      try {
        const response = await fetch(`/api/box/${encodeURIComponent(box.key)}/premium-key/validation`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        const result = await response.json().catch(() => null);
        if (version !== validationVersion.current) return false;
        const valid = response.ok && result?.available === true;
        setAvailableName(valid ? name : null);
        setRequestError(valid ? null : addressError(result?.error?.message));
        return valid;
      } catch {
        if (version === validationVersion.current)
          setRequestError(t('game.settings.boxLink.requestFailed'));
        return false;
      } finally {
        if (version === validationVersion.current) validation.current = null;
      }
    })();
    validation.current = { name, promise };
    return promise;
  }
  async function requestAddress() {
    setNameTouched(true);
    if (!canEnable || nameError || busy || desiredName === submittedName) return;
    if (!contactEmail) {
      validationVersion.current += 1;
      validation.current = null;
      setRequestError(t('game.settings.boxLink.requestEmailRequired'));
      return;
    }
    const name = desiredName;
    setBusy(true);
    setRequestError(null);
    try {
      if (!(await validateAddress())) return;
      const response = await fetch(`/api/box/${encodeURIComponent(box.key)}/premium-key/request`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        setRequestError(addressError(result?.error?.message));
        return;
      }
      if (result?.premiumKey !== `${PENDING_PREMIUM_BOX_KEY_PREFIX}${name}`)
        throw new Error('Invalid address request result');
      setAvailableName(null);
      setSubmittedName(name);
      // The previous alias no longer resolves after replacing it with pending__.
      if (typeof window !== 'undefined' && window.__ssBoxKey) {
        window.location.assign(mainSiteUrl(`/box/${encodeURIComponent(box.key)}/settings#game-address`));
        return;
      }
      void dispatch(loadSession());
    } catch {
      setRequestError(t('game.settings.boxLink.requestFailed'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <OrganizerPanelSection gameKey={box.key}
      mobile={mobile}
      id="game-address"
      collapsible
      icon={<LinkIcon />}
      title={t('game.settings.boxLink.title')}
      premium premiumFeature="address"
    >
      <div data-testid="game-settings-box-link">
        <div
          className={`box-link-panel__body${approvedName && !editing ? ' box-link-panel__body--approved' : ''}${!hasPremiumFeature(box, 'boxDomain') && !editing && !approvedName ? ' box-link-panel__body--upsell' : ''}`}
        >
          <div className="box-link-panel__controls">
            {approvedName ? (
              <>
                <Hint tone="success" testId="game-settings-box-link-approved-status">
                  <Stack gap={2} wide>
                    <Typography as="p" role="body/strong" color="inherit">
                      {t('game.settings.boxLink.approvedReady')}
                    </Typography>
                    <Typography
                      as="p"
                      role="body/strong"
                      color="inherit"
                      className="box-link-panel__approved-address"
                      testId="game-settings-box-link-approved"
                    >
                      {approvedJoinUrl}
                    </Typography>
                    <Typography as="p" role="body/sm" color="inherit">
                      <Trans
                        t={t}
                        i18nKey="game.settings.boxLink.approvedManage"
                        components={{ management: <a href={new URL('organizer#invite', approvedUrl).href} onClick={openInvitationPanel} /> }}
                      />
                    </Typography>
                  </Stack>
                </Hint>
                {!usingApprovedAddress && (
                  <Button onClick={useApprovedAddress} testId="game-settings-box-link-use-approved">
                    {t('game.settings.boxLink.useApprovedAddress')}
                  </Button>
                )}
              </>
            ) : (
              <Switch
                className="box-link-panel__toggle"
                checked={canEnable && enabled}
                disabled={!canEnable || busy}
                onCheckedChange={setEnabled}
                label={
                  <Typography as="span" role="title/card">
                    {t('game.settings.boxLink.useCustomAddress')}
                  </Typography>
                }
                hint={editing || hasPremiumFeature(box, 'boxDomain') ? undefined : t('game.settings.boxLink.toggleHint')}
                testId="game-settings-box-link-toggle"
              />
            )}
            {editing && (
              <div className="box-link-panel__editor" data-testid="game-settings-box-link-editor">
                <Input
                  label={t('game.settings.boxLink.desiredName')}
                  value={desiredName}
                  onValueChange={(value) => {
                    setAvailableName(null);
                    setNameTouched(false);
                    validationVersion.current += 1;
                    validation.current = null;
                    setDesiredName(value.toLowerCase());
                    setRequestError(null);
                  }}
                  disabled={busy}
                  placeholder="company"
                  prefix="https://"
                  suffix={`.${domain}`}
                  maxLength={PREMIUM_BOX_NAME_MAX_LENGTH}
                  pattern={/^[a-zA-Z0-9-]*$/}
                  onBlur={() => void validateAddress()}
                  error={
                    nameError && nameTouched
                      ? t(`game.settings.boxLink.nameErrors.${nameError}`)
                      : undefined
                  }
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  hint={t('game.settings.boxLink.nameFormat')}
                  testId="game-settings-box-link-name"
                />
                {requestError && <Hint testId="game-settings-box-link-error">{requestError}</Hint>}
                {availableName === desiredName &&
                  !requestError &&
                  submittedName !== desiredName && (
                    <Hint tone="success" testId="game-settings-box-link-available">
                      {t('game.settings.boxLink.addressAvailable')}
                    </Hint>
                  )}
                {!contactEmail && (
                  <Hint testId="game-settings-box-link-email-required">
                    <Trans
                      t={t}
                      i18nKey="game.settings.boxLink.requestEmailHint"
                      components={{
                        account: <a href={mainSiteUrl('/account#permanent-account')} />,
                      }}
                    />
                  </Hint>
                )}
                {submittedName && (
                  <Typography
                    as="p"
                    role="body/base"
                    aria-live="polite"
                    testId="game-settings-box-link-pending"
                  >
                    <Trans
                      t={t}
                      i18nKey="game.settings.boxLink.requestPending"
                      values={{ address: `https://${submittedName}.${domain}` }}
                      components={{ address: <strong /> }}
                    />
                  </Typography>
                )}
                <div className="box-link-panel__request">
                  <Button
                    onClick={() => void requestAddress()}
                    loading={busy}
                    disabled={!contactEmail || Boolean(nameError) || desiredName === submittedName}
                    testId="game-settings-box-link-request"
                  >
                    {t(
                      approvedName || (submittedName && desiredName !== submittedName)
                        ? 'game.settings.boxLink.requestNewAddress'
                        : 'game.settings.boxLink.requestAddress',
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
          {hasPremiumFeature(box, 'boxDomain') && (
            <aside
              className="box-link-panel__approval"
              data-testid="game-settings-box-link-approval"
            >
              <BrowserPreview address={`https://${approvedName || submittedName || desiredName || 'company'}.${domain}`}>
                <GamePreview pkg={seasonPackage} name={box.name} picture={box.picture} logo={box.logo} mode={theme} embedded selectionContext="creation" />
              </BrowserPreview>
              {approvedName && (
                <>
                  <Typography as="h3" role="title/card">
                    {t('game.settings.boxLink.changeApprovedTitle')}
                  </Typography>
                  <Typography
                    as="p"
                    role="body/base"
                    testId="game-settings-box-link-change-warning"
                  >
                    <Trans
                      t={t}
                      i18nKey="game.settings.boxLink.changeApprovedWarning"
                      components={{ warning: <strong /> }}
                    />
                  </Typography>
                  {!editing && canEnable && (
                    <Button
                      variant="secondary"
                      onClick={() => setEnabled(true)}
                      testId="game-settings-box-link-change"
                    >
                      {t('game.settings.boxLink.requestNewAddress')}
                    </Button>
                  )}
                </>
              )}
              {editing && (
                <>
                  <Typography as="h3" role="title/card">
                    {t('game.settings.boxLink.approvalTitle')}
                  </Typography>
                  <Typography as="p" role="body/base">
                    {t('game.settings.boxLink.approvalTiming')}
                  </Typography>
                  <Typography as="p" role="body/base">
                    {t('game.settings.boxLink.approvalBranding')}
                  </Typography>
                  <Typography as="p" role="body/base">
                    <Trans
                      t={t}
                      i18nKey="game.settings.boxLink.approvalProof"
                      values={{ email: support }}
                      components={{
                        contact: <a href={`mailto:${support}`} />,
                        account: <a href={mainSiteUrl('/account#permanent-account')} />,
                      }}
                    />
                  </Typography>
                </>
              )}
            </aside>
          )}
          {!hasPremiumFeature(box, 'boxDomain') && !editing && !approvedName && (
            <PremiumUpsell feature="address" testId="game-settings-box-link-premium-hint">
              <div className="premium-upsell__example-group">
                <div className="premium-upsell__example">
                  <span>{t('premiumUpsell.address.exampleLabel')}</span>
                  <span className="premium-upsell__example-address">https://<strong>company.{domain}</strong></span>
                </div>
                <p className="premium-upsell__example-note">
                  <svg viewBox="0 0 58 58" fill="none" aria-hidden="true" focusable="false">
                    <path d="M52 49C24 52 14 29 26 27c15-3 16 21 0 17C11 40 6 21 10 7m-5 7 5-7 4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{t('premiumUpsell.address.exampleNote')}</span>
                </p>
              </div>
            </PremiumUpsell>
          )}
        </div>
      </div>
    </OrganizerPanelSection>
  );
}
